import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Globe2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";

const TAG_STYLE = {
  Release:      "text-[#25604d] bg-[#d9f2e5] border-[#a5dbc0]",
  Update:       "text-primary bg-accent/40 border-accent",
  Announcement: "text-[#7a5a12] bg-[#fdf2c9] border-[#f0d98c]",
};

export default function WhatsNew() {
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/announcements").then((r) => setItems(r.data)).catch(() => {});
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  const programName = (id) => programs.find((p) => p.id === id)?.name || id;
  const chips = useMemo(() => [...new Set(items.map((i) => i.program_id))], [items]);
  const filtered = filter === "all" ? items : items.filter((i) => i.program_id === filter);

  return (
    <div>
      <PageHeader
        eyebrow="The Dispatch"
        title="What's New"
        subtitle="Releases, workflow updates and announcements across every program you're part of."
        testid="whatsnew-header" />

      {chips.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="whatsnew-program-filter">
          <button onClick={() => setFilter("all")} data-testid="whatsnew-filter-all"
            className={`text-xs font-medium rounded-full border px-3.5 py-1.5 transition-all flex items-center gap-1.5 ${
              filter === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground/70 hover:border-primary/40"}`}>
            <Globe2 className="w-3.5 h-3.5" /> All programs
          </button>
          {chips.map((pid) => (
            <button key={pid} onClick={() => setFilter(pid)} data-testid={`whatsnew-filter-${pid}`}
              className={`text-xs font-medium rounded-full border px-3.5 py-1.5 transition-all ${
                filter === pid ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground/70 hover:border-primary/40"}`}>
              {programName(pid)}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={Sparkles} title="Nothing new right now" hint="Check back later — updates are posted regularly." />
      ) : (
        <ol className="relative border-l-2 border-border ml-3 space-y-6 stagger" data-testid="whatsnew-feed">
          {filtered.map((a) => (
            <li key={a.id} className="pl-8 relative">
              <span className="absolute -left-[9px] top-3 w-4 h-4 rounded-full bg-primary ring-4 ring-background" />
              <article className="kp-card p-5 kp-card-lift">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-[10px] font-mono uppercase tracking-wider font-semibold rounded-md border px-2 py-0.5 ${TAG_STYLE[a.tag] || ""}`}>{a.tag}</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider rounded-full bg-secondary/50 text-primary px-2.5 py-0.5">{programName(a.program_id)}</span>
                  <span className="text-[11px] font-mono text-muted-foreground ml-auto">{a.date}</span>
                </div>
                <h3 className="font-display text-xl text-foreground leading-tight">{a.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{a.description}</p>
              </article>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
